// src/plugins/fun-picker.plugin.js
import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import { createDropdown } from '@ckeditor/ckeditor5-ui';

// -------------------------------
// Tenor Provider (GIF-only)
// -------------------------------
class TenorProvider {
	constructor(apiKey) {
		this.key = apiKey;
		this.limit = 24;
	}

	async search(q, pos) {
		const url =
			'https://tenor.googleapis.com/v2/search' +
			'?q=' + encodeURIComponent(q) +
			'&key=' + encodeURIComponent(this.key) +
			'&limit=' + this.limit +
			'&media_filter=gif' +
			(pos ? '&pos=' + encodeURIComponent(pos) : '');

		const res = await fetch(url);
		const json = await res.json();

		const items = (json.results || []).map(result => {
			const fm = result.media_formats || {};
			const gif = fm.gif || {};
			const tiny = fm.tinygif || {};

			return {
				id: result.id,
				title: result.content_description ? result.content_description.slice(0, 15) : '',
				preview: tiny.url || gif.url || '',
				gifUrl: gif.url || ''
			};
		});

		return { items, next: json.next || '' };
	}
}

// -------------------------------
// Plugin
// -------------------------------
export default class FunPickerPlugin extends Plugin {
	static get pluginName() {
		return 'FunPickerPlugin';
	}

	init() {
		const editor = this.editor;
		const cfg = editor.config.get('funPicker') || {};

		const apiKey = cfg.apiKey;
		const onSelect = typeof cfg.onSelect === 'function' ? cfg.onSelect : null;
		const isEnabledFn = typeof cfg.isEnabled === 'function' ? cfg.isEnabled : null;

		if (!apiKey) {
			console.warn('[FunPickerPlugin] Missing funPicker.apiKey');
			return;
		}

		const tenor = new TenorProvider(apiKey);

		editor.ui.componentFactory.add('fun', locale => {
			const dropdownView = createDropdown(locale);
			const buttonView = dropdownView.buttonView;

			const iconSvg =
				'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 15" fill="none">' +
				'<path fill="currentColor" d="M2.5 10.5H2V11H2.5V10.5ZM4.5 10.5V11H5V10.5H4.5ZM13.5 3.5H14V3.29289L13.8536 3.14645L13.5 3.5ZM10.5 0.5L10.8536 0.146447L10.7071 0H10.5V0.5ZM2 6V10.5H3V6H2ZM2.5 11H4.5V10H2.5V11ZM5 10.5V8.5H4V10.5H5ZM3 7H5V6H3V7ZM2 5V1.5H1V5H2ZM13 3.5V5H14V3.5H13ZM2.5 1H10.5V0H2.5V1ZM10.1464 0.853553L13.1464 3.85355L13.8536 3.14645L10.8536 0.146447L10.1464 0.853553ZM2 1.5C2 1.22386 2.22386 1 2.5 1V0C1.67157 0 1 0.671573 1 1.5H2ZM1 12V13.5H2V12H1ZM2.5 15H12.5V14H2.5V15ZM14 13.5V12H13V13.5H14ZM12.5 15C13.3284 15 14 14.3284 14 13.5H13C13 13.7761 12.7761 14 12.5 14V15ZM1 13.5C1 14.3284 1.67157 15 2.5 15V14C2.22386 14 2 13.7761 2 13.5H1ZM6 7H9V6H6V7ZM6 11H9V10H6V11ZM7 6.5V10.5H8V6.5H7ZM10.5 7H13V6H10.5V7ZM10 6V11H11V6H10ZM10.5 9H12V8H10.5V9Z" />' +
				'</svg>';

			buttonView.set({
				label: 'GIFs',
				icon: iconSvg,
				withText: false,
				tooltip: 'GIF Picker'
			});

			buttonView.isEnabled =
				!editor.isReadOnly &&
				(!isEnabledFn || (() => { try { return !!isEnabledFn(); } catch { return false; } })());

			// -----------------------
			// STATE
			// -----------------------
			let built = false;
			let isLoading = false;
			let hasMore = true;

			let gridEl = null;
			let inputEl = null;
			let loaderEl = null;

			let currentQuery = 'funny';
			let nextPos = '';

			// -----------------------
			// Helpers
			// -----------------------
			const setLoading = loading => {
				isLoading = loading;
				if (loaderEl) loaderEl.style.display = loading ? 'block' : 'none';
			};

			const showEmptyMessage = msg => {
				if (!gridEl) return;
				gridEl.innerHTML = '';

				const info = document.createElement('div');
				info.textContent = msg;
				info.style.textAlign = 'center';
				info.style.padding = '16px';
				info.style.fontSize = '12px';
				info.style.color = '#777';

				gridEl.appendChild(info);
			};

			const clearGrid = () => {
				if (gridEl) gridEl.innerHTML = '';
			};

			// -----------------------
			// Load GIFs (infinite scroll)
			// -----------------------
			const loadResults = append => {
				if (isLoading) return;

				if (!append) {
					clearGrid();
					hasMore = true;
					nextPos = '';
					if (gridEl) gridEl.scrollTop = 0;
				}

				setLoading(true);

				tenor.search(currentQuery, append ? nextPos : '')
					.then(res => {
						const items = res.items || [];
						nextPos = res.next || '';
						hasMore = !!nextPos && items.length > 0;

						if (!append && items.length === 0) {
							showEmptyMessage('No GIFs found. Try another search.');
							return;
						}

						items.forEach(item => {
							if (!item.gifUrl) return;

							const img = document.createElement('img');
							img.src = item.preview;
							img.style.width = '100%';
							img.style.height = '80px';
							img.style.objectFit = 'cover';
							img.style.cursor = 'pointer';
							img.style.borderRadius = '4px';

							img.onclick = () => {
								onSelect &&
								onSelect(
									{
										type: 'gif',
										url: item.gifUrl,
										previewUrl: item.preview,
										id: item.id,
										title: item.title
									},
									editor
								);

								dropdownView.isOpen = false;
							};

							gridEl.appendChild(img);
						});

						// ⭐ Auto-scroll ~one row (~100px) when appending
						if (append && gridEl) {
							gridEl.scrollTop += 100;
						}
					})
					.catch(err => {
						console.error('[FunPickerPlugin] Tenor search failed', err);

						if (!append) {
							showEmptyMessage('Could not load GIFs. Please try again.');
						}
					})
					.finally(() => setLoading(false));
			};

			// -----------------------
			// Panel UI
			// -----------------------
			const createPanel = () => {
				const panelEl = dropdownView.panelView.element;
				panelEl.style.padding = '0';

				const wrap = document.createElement('div');
				wrap.style.padding = '8px';
				wrap.style.width = '260px';
				wrap.style.background = '#fff';
				wrap.style.display = 'flex';
				wrap.style.flexDirection = 'column';
				wrap.style.gap = '6px';

				// search input
				inputEl = document.createElement('input');
				inputEl.type = 'text';
				inputEl.placeholder = 'Search GIFs...';
				inputEl.style.width = '100%';
				inputEl.style.padding = '6px 8px';
				inputEl.style.border = '1px solid #ddd';
				inputEl.style.borderRadius = '4px';
				inputEl.style.fontSize = '13px';

				// grid
				gridEl = document.createElement('div');
				gridEl.style.maxHeight = '240px';
				gridEl.style.overflow = 'auto';
				gridEl.style.display = 'grid';
				gridEl.style.gridTemplateColumns = 'repeat(3,1fr)';
				gridEl.style.gap = '6px';

				// loader
				loaderEl = document.createElement('div');
				loaderEl.style.textAlign = 'center';
				loaderEl.style.padding = '6px';
				loaderEl.style.fontSize = '12px';
				loaderEl.style.color = '#666';
				loaderEl.style.display = 'none';
				loaderEl.textContent = 'Loading GIFs…';

				// ⭐ Infinite scroll listener
				gridEl.addEventListener('scroll', () => {
					const threshold = 140;
					const scrollPos = gridEl.scrollTop + gridEl.clientHeight;

					if (!isLoading && hasMore && scrollPos >= gridEl.scrollHeight - threshold) {
						loadResults(true);
					}
				});

				wrap.appendChild(inputEl);
				wrap.appendChild(gridEl);
				wrap.appendChild(loaderEl);
				panelEl.appendChild(wrap);

				// search debounce
				let searchTO = null;
				inputEl.addEventListener('input', () => {
					clearTimeout(searchTO);
					searchTO = setTimeout(() => {
						currentQuery = inputEl.value || 'funny';
						nextPos = '';
						hasMore = true;
						loadResults(false);
					}, 300);
				});

				// First load
				loadResults(false);
			};

			// Build panel on render
			dropdownView.on('render', () => {
				if (!built) {
					built = true;
					createPanel();
				}
			});

			// focus search on open
			dropdownView.on('change:isOpen', (_, __, isOpen) => {
				if (isOpen && inputEl) {
					inputEl.focus();
					gridEl.scrollTop = 0;
				}
			});

			return dropdownView;
		});
	}
}
